import { Browser } from "@capacitor/browser";
import { useState } from "react";

import { invalidateCurrentOrderDetails } from "@/modules/cart/useCartDetails";
import { storage } from "@/modules/storage";
import { deleteOrdersCurrentLineitemsByShopDisplayOrderLineItemId } from "api/methods";
import { components } from "api/generated";
import { ShopDisplayType } from "api/types";

import { clearAcknowledgedPopupPromotionIds } from "./promotionModalAcknowledgment";
import { isNativeMobileApp } from "./platform.helpers";
import { isUnsafeUrl } from "./validators";

/** @deprecated Use acknowledgePopupPromotionIds / clearAcknowledgedPopupPromotionIds */
export const setPromotionModalSeen = (seen: boolean) => {
  if (!seen) {
    clearAcknowledgedPopupPromotionIds();
  }
};

/** @deprecated Use hasUnacknowledgedPopups with active popup promotion ids */
export const getPromotionModalSeen = () => {
  return false;
};

export {
  acknowledgePopupPromotionIds,
  clearAcknowledgedPopupPromotionIds,
  filterUnacknowledgedPopupPromotionIds,
  getAcknowledgedPopupPromotionIds,
  hasUnacknowledgedPopups,
  reconcileLegacyPopupAcknowledgment,
} from "./promotionModalAcknowledgment";

export const hasInactiveItems = (
  details: components["schemas"]["ShopDisplayOrderResult"],
): boolean => {
  if (!details?.shopDisplayOrderLineItems?.length) {
    return false;
  }

  return details.shopDisplayOrderLineItems.some((item) => !item.active);
};

export const removeArchiveItems = async (
  details: components["schemas"]["ShopDisplayOrderResult"],
) => {
  let itemsArchived = 0;

  if (details?.shopDisplayOrderLineItems?.length) {
    const archivedItems = details.shopDisplayOrderLineItems.filter((item) => !item.active);
    if (archivedItems.length) {
      itemsArchived = archivedItems.length;
      const results = await Promise.allSettled(
        archivedItems.map(
          async (archiveItem) =>
            await deleteOrdersCurrentLineitemsByShopDisplayOrderLineItemId({
              shopDisplayOrderLineItemId: archiveItem.id,
            }),
        ),
      );

      await invalidateCurrentOrderDetails();

      const isAnyFailure = results.some(({ status }) => status === "rejected");
      if (isAnyFailure) {
        throw new Error("Some items failed to be removed.");
      }
    }
  }

  return itemsArchived;
};

export const buildPlatformUrl = (
  scheme: string | null | undefined,
  subdomain: string | null | undefined,
  host: string | null | undefined,
  page?: string | null | undefined,
) => {
  if (page == "checkout" && isNativeMobileApp()) {
    return `${globalThis.location.origin}`;
  }
  const uri = (host ?? "").includes(subdomain ?? "") ? host : `${subdomain}.${host}`;
  return `${scheme}://${uri}`;
};

export const getAdminUrl = () =>
  globalThis.location.protocol +
  "//" +
  globalThis.location.host.replace(/^[^.]*\.?/, "admin.").replace(/\w+\.com/, "repeatmd.app");

export const getPDPUrl = (listingType: number, listingId: string) => {
  if (listingType === ShopDisplayType.Membership) {
    return `/pdp/membership/${listingId}`;
  } else if (listingType === ShopDisplayType.GiftCard) {
    return `/shop/gift-cards`;
  } else if (
    listingType === ShopDisplayType.Product ||
    listingType === ShopDisplayType.ProductFromExternalSource
  ) {
    return `/pdp/product/${listingId}`;
  } else {
    return `/pdp/package/${listingId}`;
  }
};

export function useStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = storage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (err) {
      console.error(`Error reading storage key "${key}":`, err);
      return initialValue;
    }
  });

  const setValue: React.Dispatch<React.SetStateAction<T>> = (value) => {
    try {
      const valueToStore =
        typeof value === "function" ? (value as (prev: T) => T)(storedValue) : value;

      setStoredValue(valueToStore);

      if (valueToStore === null || valueToStore === undefined) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (err) {
      console.error(`Error writing storage key "${key}":`, err);
    }
  };

  return [storedValue, setValue] as const;
}

export const calculateRepeatCashEligibleAmount = (
  cartDetails: components["schemas"]["ShopDisplayOrderResult"],
): number => {
  if (!cartDetails.shopDisplayOrderLineItems) {
    return 0;
  }

  if (typeof cartDetails.repeatCashEligibleAmountInCents === "number") {
    return cartDetails.repeatCashEligibleAmountInCents;
  }

  const totalItemsAmount = cartDetails.shopDisplayOrderLineItems
    .filter(({ displayType }) => displayType !== ShopDisplayType.GiftCard)
    .reduce((total, { cost, discountTotal }) => total + cost - (discountTotal ?? 0), 0);

  return Math.max(0, totalItemsAmount);
};

/**
 * Total RepeatCash the backend is actually spending on this order. Purchased
 * RepeatCash is tender, so leftover balance covers tax and shipping on top of
 * merchandise (DiscountService allocates the three buckets and subtracts all of
 * them from EstimatedTotal). Showing only the merchandise bucket understates the
 * debit the patient sees on the completed order.
 */
export const getTotalRepeatCashApplied = (
  cartDetails: components["schemas"]["ShopDisplayOrderResult"],
): number =>
  cartDetails.repeatCashAppliedToCart +
  cartDetails.repeatCashAppliedToTax +
  cartDetails.repeatCashAppliedToShipping;

export const getPossessiveForm = (name?: string | null): string => {
  if (!name) {
    return "";
  }

  return name.endsWith("s") ? `${name}'` : `${name}'s`;
};

export const openUrl = async (url: string) => {
  if (isUnsafeUrl(url.toString())) {
    return;
  }

  if (!isNativeMobileApp()) {
    // Web browser: open in new tab
    window.open(url.toString(), "_blank", "noopener,noreferrer");
    return;
  }

  // Native (iOS/Android WebView):
  const urlObj = new URL(url.toString());
  const isRepeatMdDomain = urlObj.hostname.endsWith("repeatmd.app");

  if (isRepeatMdDomain) {
    // For repeatmd.app domains → navigate within the app
    // Dispatch custom event that appUrlListener will handle
    // This prevents the link from opening in the phone's browser
    const event = new CustomEvent("internalUrlNavigation", {
      detail: url.toString(),
    });
    globalThis.dispatchEvent(event);
  } else {
    // For external domains → open in system browser
    await Browser.open({ url: url.toString() });
  }
};
